import { GlobalCopilotFloatieButton } from '@ms/yammer-copilot/dist/integration';
import { useIsTopLevelFeaturePage } from '@ms/yammer-hooks/dist/navigation';
import { useTranslations } from '@ms/yammer-i18n-react';
import { useEndActiveSession } from '@ms/yammer-search-components/dist/telemetry';
import CommonStrings from '@ms/yammer-strings/dist/Common';
import PanesStrings from '@ms/yammer-strings/dist/Panes';
import { MultiColumnAppLayout } from '@ms/yammer-web-core-ui/dist/components/AppLayout';
import { ContainerWidthProvider } from '@ms/yammer-web-core-ui/dist/util/responsiveness';
import { articleBasePath, articleEditPageMatchPath, articlePageMatchPath } from '@ms/yammer-web-support/dist/urls';
import { useUIFeatures } from '@ms/yammer-web-ui-features/dist/features';
import { useTeamsClientContext } from 'components/TeamsClientProvider/hooks';
import React, { FC } from 'react';
import { useRouteMatch } from 'react-router-dom';

import { AppLeftNav } from './AppLeftNav';
import ArticleFooter from './LoadableArticleFooter';
import LoadableTeamsSubHeader from './LoadableTeamsSubHeader';
import { useBackButtonProps, useGetBanners } from './hooks';

const AppLayout: FC = ({ children }) => {
  useEndActiveSession();
  /**
   * This custom Article logic is temporary until we refactor CoreUI's AppLayout so
   * content/page-level templates allow slot props like `footer`.
   * https://dev.azure.com/yammer/engineering/_workitems/edit/295328
   */
  const isAnyArticlesPage = !!useRouteMatch(articleBasePath);
  const isArticleEditPage = !!useRouteMatch(articleEditPageMatchPath);
  const isArticlePage = !!useRouteMatch(articlePageMatchPath) && !isArticleEditPage;
  const backgroundColor = isAnyArticlesPage ? 'white' : undefined;
  const footer = isArticlePage ? <ArticleFooter /> : undefined;

  const { shouldHideSubHeader } = useTeamsClientContext();
  const subHeader = !shouldHideSubHeader ? <LoadableTeamsSubHeader /> : null;

  const banners = useGetBanners();

  const isLeftSidebarEnabled = useUIFeatures((features) => features.navigation?.leftSidebar);

  const leftNav = isLeftSidebarEnabled ? <AppLeftNav /> : null;

  const openAriaLabel = useTranslations(PanesStrings).showAdditionalInformationPane();
  const commonStrings = useTranslations(CommonStrings);
  const backButtonAriaLabel = commonStrings.commonGoBack();
  const closeButtonAriaLabel = commonStrings.commonClose();

  const isTopLevelFeaturePage = useIsTopLevelFeaturePage();
  const { goBack, showButton: hasBackButton } = useBackButtonProps();

  return (
    <ContainerWidthProvider>
      <MultiColumnAppLayout
        header={<GlobalCopilotFloatieButton />}
        headerDepth="depth0"
        subHeader={subHeader}
        banner={banners}
        backButtonAriaLabel={backButtonAriaLabel}
        hasBackButton={hasBackButton}
        onBackButtonClick={goBack}
        leftNav={leftNav}
        hasHeroBackground={isTopLevelFeaturePage}
        openContentSidebarAriaLabel={openAriaLabel}
        closeContentSidebarAriaLabel={closeButtonAriaLabel}
        footer={footer}
        backgroundColor={backgroundColor}
      >
        {children}
      </MultiColumnAppLayout>
    </ContainerWidthProvider>
  );
};

export default AppLayout;
